import { Fragment, ReactNode } from 'react';
import { Link, Typography, TypographyProps } from '@mui/material';
import LinkifyIt from 'linkify-it';

interface LinkifyTextProps extends Omit<TypographyProps, 'children'> {
    children: string;
}

/**
 * Renders plain text with any links turned into clickable `<Link>`s. Backed by
 * `linkify-it` for robust detection of https/http, `www.`, bare domains, and emails.
 *
 * Config: `fuzzyLink` covers scheme-less links (`www.`/bare domains), `fuzzyEmail`
 * covers `mailto:`-less emails, IPs are intentionally excluded to avoid false positives.
 */
const linkify = new LinkifyIt({}, { fuzzyLink: true, fuzzyEmail: true, fuzzyIP: false });

/**
 * Force web links to `https://`. Covers both scheme-less links (which linkify-it
 * emits as `http://`) and explicitly-typed `http://`.
 */
function toSecureHref(url: string): string {
    return url.startsWith('http://') ? `https://${url.slice('http://'.length)}` : url;
}

export default function LinkifyText({ children, sx, ...typographyProps }: Readonly<LinkifyTextProps>) {
    const matches = linkify.match(children) ?? [];

    const content: ReactNode[] = [];
    let cursor = 0;
    matches.forEach((match) => {
        if (match.index > cursor) {
            content.push(<Fragment key={`text-${match.index}`}>{children.slice(cursor, match.index)}</Fragment>);
        }
        content.push(
            <Link
                key={`link-${match.index}`}
                href={toSecureHref(match.url)}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover">
                {match.text}
            </Link>
        );
        cursor = match.lastIndex;
    });
    if (cursor < children.length) {
        content.push(<Fragment key="text-end">{children.slice(cursor)}</Fragment>);
    }

    return (
        // Merge the caller's `sx` over the base wrapping (array form) rather than letting it override.
        <Typography
            sx={[{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }, ...(Array.isArray(sx) ? sx : [sx])]}
            {...typographyProps}>
            {content}
        </Typography>
    );
}
